import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { createAppTheme, type ResolvedThemeMode } from './theme';
import Layout from './components/Layout';
import PwaUpdatePrompt from './components/PwaUpdatePrompt';
import AppErrorBoundary from './components/AppErrorBoundary';
// Route-level code splitting: each page is its own chunk, so the initial
// bundle only pays for the framework + shell + whatever the landing route
// pulls in. The Suspense fallback below mirrors the loading UI already used
// during DB init so route transitions feel consistent with cold start.
const HomePage = lazy(() => import('./pages/HomePage'));
const PrayersPage = lazy(() => import('./pages/PrayersPage'));
const CheckInsPage = lazy(() => import('./pages/CheckInsPage'));
const TenthStepPage = lazy(() => import('./pages/TenthStepPage'));
const SponsorTasksPage = lazy(() => import('./pages/SponsorTasksPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProgressPage = lazy(() => import('./pages/ProgressPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const InvitesPage = lazy(() => import('./pages/InvitesPage'));
const RedeemInvitePage = lazy(() => import('./pages/RedeemInvitePage'));
const SponsorshipPage = lazy(() => import('./pages/SponsorshipPage'));
const SponseeDataPage = lazy(() => import('./pages/SponseeDataPage'));
import { initializeDatabase } from './services/db';
import { requestNotificationPermission } from './services/notifications';
import { SettingsProvider, useSettings } from './services/settingsContext';

function RouteFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
}

function NotFoundPage() {
  return (
    <Box sx={{ textAlign: 'center', mt: 8 }}>
      <Typography variant="h3" gutterBottom>
        404
      </Typography>
      <Typography variant="h5" gutterBottom>
        Page not found
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        The page you were looking for isn&apos;t here.
      </Typography>
      <Button component={Link} to="/" variant="contained">
        Back to Home
      </Button>
    </Box>
  );
}

function ThemedShell() {
  const { settings } = useSettings();
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  let resolvedMode: ResolvedThemeMode;
  if (settings?.themeMode === 'light') resolvedMode = 'light';
  else if (settings?.themeMode === 'dark') resolvedMode = 'dark';
  else resolvedMode = prefersDark ? 'dark' : 'light';

  const theme = createAppTheme(resolvedMode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PwaUpdatePrompt />
      <Layout>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/prayers" element={<PrayersPage />} />
            <Route path="/check-ins" element={<CheckInsPage />} />
            <Route path="/sponsor" element={<SponsorTasksPage />} />
            <Route path="/tenth-step" element={<TenthStepPage />} />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/invites" element={<InvitesPage />} />
            <Route path="/invite" element={<RedeemInvitePage />} />
            <Route path="/sponsorship" element={<SponsorshipPage />} />
            <Route
              path="/sponsorship/sponsee/:sponseeId"
              element={<SponseeDataPage />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Layout>
    </ThemeProvider>
  );
}

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        await initializeDatabase();
        // Permission request can be denied; that's not a fatal error so we
        // only surface a hard initialization failure (e.g. IDB unavailable).
        await requestNotificationPermission();
      } catch (error) {
        console.error('Initialization error:', error);
        setInitError(
          error instanceof Error ? error.message : 'Initialization failed.',
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Couldn&apos;t start the app
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {initError}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The most common cause is an unsupported browser or private-browsing
          mode that blocks IndexedDB. Try reloading, or open this page in a
          regular browsing window.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            window.location.reload();
          }}
        >
          Reload
        </Button>
      </Box>
    );
  }

  return (
    <AppErrorBoundary>
      <SettingsProvider>
        <ThemedShell />
      </SettingsProvider>
    </AppErrorBoundary>
  );
}
